import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Copy,
  ExternalLink,
  FileText,
  FolderPlus,
  Loader2,
  PanelLeftClose,
  PanelLeftOpen,
  RefreshCw,
  RotateCw,
  Search,
  Sparkles,
  Trash2,
  Upload,
  Wrench,
} from "lucide-react";
import { MediaFolderTree, mergeFolderPaths } from "@/components/admin/MediaFolderTree";
import { NewMediaFolderDialog } from "@/components/admin/NewMediaFolderDialog";
import { CaptionEditorDialog } from "@/components/admin/CaptionEditorDialog";
import { ApiError } from "@/lib/api";
import {
  detectMediaType,
  MediaUploadError,
  MULTIPART_RESTARTED_MESSAGE,
  uploadMedia,
} from "@/lib/uploads";
import type { MediaKind } from "@/lib/media-buckets";
import {
  destinationPreview,
  MediaDestinationSelect,
  mediaDestinationOptions,
  mediaFolderLabel,
  pickDefaultDestination,
  splitDestination,
  useMediaDestinationSources,
} from "@/components/admin/MediaDestinationSelect";
import {
  backfillMediaAssets,
  deleteMediaAsset,
  generateMissingThumbnails,
  getMediaUsage,
  updateMediaAsset,
  type MediaUsage,
} from "@/lib/media-library.functions";
import {
  assetBucketAndPath,
  formatBytes,
  isCaptionsAsset,
  isVideoAsset,
  MEDIA_ACCEPT_ATTRIBUTE,
  MEDIA_MIME_FILTERS,
  MEDIA_PAGE_SIZE,
  mediaAssetLabel,
  mediaAssetsQueryOptions,
  mediaAssetUrl,
  mediaBucketsQueryOptions,
  mediaFoldersQueryOptions,
  mediaKindOfType,
  mediaUsageReportQueryOptions,
  type MediaAsset,
} from "@/lib/media-library.queries";
import { MediaAssetGrid } from "@/components/admin/MediaAssetGrid";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { FormDialog } from "@/components/admin/FormDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/admin/media")({
  component: MediaLibraryAdmin,
});

function MediaLibraryAdmin() {
  const qc = useQueryClient();

  const [search, setSearch] = useState("");
  const q = useDebouncedValue(search, 300);
  const [folder, setFolder] = useState("all");
  const [mime, setMime] = useState("all");
  const [page, setPage] = useState(0);
  const [detail, setDetail] = useState<MediaAsset | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  // Folders created this session, shown at once even before the refetch lands
  // (and even when listMediaFolders is not deployed yet).
  const [createdPaths, setCreatedPaths] = useState<string[]>([]);
  const [treeCollapsed, setTreeCollapsed] = useState(false);

  useEffect(() => {
    setPage(0);
  }, [q, folder, mime]);

  const params = useMemo(
    () => ({
      q: q.trim() || undefined,
      folder: folder === "all" ? undefined : folder,
      mime: mime === "all" ? undefined : mime,
      limit: MEDIA_PAGE_SIZE,
      offset: page * MEDIA_PAGE_SIZE,
    }),
    [q, folder, mime, page],
  );

  const { data, isFetching, isError, error } = useQuery(mediaAssetsQueryOptions(params));
  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const facets = useMemo(() => data?.folders ?? [], [data]);

  // Empty folders come from listMediaFolders; until that action is deployed
  // (query resolves to null) the registry bucket names stand in for it.
  const { data: buckets = [] } = useQuery(mediaBucketsQueryOptions());
  const { data: serverFolders } = useQuery(mediaFoldersQueryOptions());
  const knownFolderPaths = useMemo(
    () => [...(serverFolders ?? buckets.map((b) => b.name)), ...createdPaths],
    [serverFolders, buckets, createdPaths],
  );
  const treeFacets = useMemo(
    () => mergeFolderPaths(facets, knownFolderPaths),
    [facets, knownFolderPaths],
  );
  const allFolderPaths = useMemo(() => treeFacets.map((f) => f.folder), [treeFacets]);

  // Usage badges come from a separate scan so a slow report never blocks the grid.
  const { data: usageReport } = useQuery({
    // Ask for exactly the tiles on screen, so badges follow every filter.
    ...mediaUsageReportQueryOptions({ ids: items.map((i) => i.id) }),
    enabled: items.length > 0,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["media_assets"] });

  const onFolderCreated = (path: string) => {
    setNewFolderOpen(false);
    setCreatedPaths((prev) => (prev.includes(path) ? prev : [...prev, path]));
    toast.success(`Folder ${path} created`);
    void qc.invalidateQueries({ queryKey: ["media_folders"] });
    void qc.invalidateQueries({ queryKey: ["media_assets"] });
    setFolder(path);
  };

  const totalAssets = useMemo(() => facets.reduce((sum, f) => sum + f.count, 0), [facets]);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-col gap-3 space-y-0 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <CardTitle>Media Library</CardTitle>
            <CardDescription>
              {totalAssets > 0
                ? `${totalAssets} file${totalAssets === 1 ? "" : "s"} across ${facets.length} folder${facets.length === 1 ? "" : "s"}.`
                : "Central store for images, video and PDFs used across the site."}
            </CardDescription>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative min-w-[200px] flex-1">
              <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, title or tag…"
                className="pl-8"
              />
            </div>
            <Select value={mime} onValueChange={setMime}>
              <SelectTrigger className="w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MEDIA_MIME_FILTERS.map((f) => (
                  <SelectItem key={f.value} value={f.value}>
                    {f.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" onClick={() => setNewFolderOpen(true)}>
              <FolderPlus className="mr-1 h-4 w-4" /> New folder
            </Button>
            <Button size="sm" onClick={() => setUploadOpen(true)}>
              <Upload className="mr-1 h-4 w-4" /> Upload
            </Button>
            <MaintenanceMenu onDone={invalidate} />
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Below lg the folder tree gives way to the flat chips. */}
          <div className="lg:hidden">
            <FolderChips
              facets={treeFacets}
              active={folder}
              onChange={setFolder}
              totalAssets={totalAssets}
            />
          </div>

          <div className="flex items-start gap-4">
            {/* Folder navigation — mirrors the S3 prefix tree under public/. */}
            <aside
              className={cn(
                "hidden shrink-0 lg:block",
                treeCollapsed ? "w-auto" : "w-60",
              )}
            >
              <div className="sticky top-2 rounded-lg border bg-background/60 p-2">
                <div className="mb-1 flex items-center justify-between gap-2">
                  {!treeCollapsed && (
                    <span className="px-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      Folders
                    </span>
                  )}
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="h-6 w-6"
                    title={treeCollapsed ? "Expand folder panel" : "Collapse folder panel"}
                    aria-label={treeCollapsed ? "Expand folder panel" : "Collapse folder panel"}
                    onClick={() => setTreeCollapsed((v) => !v)}
                  >
                    {treeCollapsed ? (
                      <PanelLeftOpen className="h-4 w-4" />
                    ) : (
                      <PanelLeftClose className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                {!treeCollapsed && (
                  <MediaFolderTree
                    facets={treeFacets}
                    active={folder}
                    onChange={setFolder}
                    totalAssets={totalAssets}
                  />
                )}
              </div>
            </aside>

            <div className="min-w-0 flex-1 space-y-4">
              {isError ? (
                <div className="grid place-items-center rounded-lg border border-dashed py-16 text-sm text-destructive">
                  {error instanceof Error ? error.message : "Could not load the media library."}
                </div>
              ) : (
                <MediaAssetGrid
                  assets={items}
                  usageCounts={usageReport?.counts}
                  selectedId={detail?.id ?? null}
                  onSelect={setDetail}
                  isLoading={isFetching}
                  emptyMessage={
                    folder !== "all" && !q && mime === "all"
                      ? "This folder is empty. Use Upload to add files here."
                      : q || folder !== "all" || mime !== "all"
                        ? "No media matches these filters."
                        : "No media yet — upload a file, or run Backfill registry to index what is already in S3."
                  }
                />
              )}
            </div>
          </div>

          <div className="flex items-center justify-between gap-2 text-sm text-muted-foreground">
            <span>
              {total > 0
                ? `Showing ${page * MEDIA_PAGE_SIZE + 1}–${Math.min((page + 1) * MEDIA_PAGE_SIZE, total)} of ${total}`
                : "No results"}
              {isFetching && <Loader2 className="ml-2 inline h-3 w-3 animate-spin" />}
            </span>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                Previous
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={(page + 1) * MEDIA_PAGE_SIZE >= total}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <AssetDetailSheet
        asset={detail}
        onClose={() => setDetail(null)}
        onChanged={invalidate}
        onDeleted={() => {
          setDetail(null);
          invalidate();
        }}
      />

      <UploadDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        defaultFolder={folder}
        folderPaths={allFolderPaths}
        onFolderCreated={(path) => {
          setCreatedPaths((prev) => (prev.includes(path) ? prev : [...prev, path]));
          toast.success(`Folder ${path} created`);
        }}
        onDone={invalidate}
      />

      <NewMediaFolderDialog
        open={newFolderOpen}
        onOpenChange={setNewFolderOpen}
        buckets={buckets}
        knownPaths={allFolderPaths}
        highlighted={folder}
        onCreated={onFolderCreated}
      />
    </div>
  );
}

// ------------------------------------------------------------- folder chips

function FolderChips({
  facets,
  active,
  onChange,
  totalAssets,
}: {
  facets: { folder: string; count: number }[];
  active: string;
  onChange: (folder: string) => void;
  totalAssets: number;
}) {
  if (facets.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      <FolderChip label="All" count={totalAssets} active={active === "all"} onClick={() => onChange("all")} />
      {facets.map((f) => (
        <FolderChip
          key={f.folder}
          label={f.folder}
          count={f.count}
          active={active === f.folder}
          onClick={() => onChange(f.folder)}
        />
      ))}
    </div>
  );
}

function FolderChip({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-full border px-3 py-1 text-xs transition",
        active
          ? "border-primary bg-primary text-primary-foreground"
          : "bg-background hover:bg-secondary",
      )}
    >
      {label}
      <span className={cn("ml-1.5", active ? "opacity-80" : "text-muted-foreground")}>{count}</span>
    </button>
  );
}

// ------------------------------------------------------------ detail sheet

function AssetDetailSheet({
  asset,
  onClose,
  onChanged,
  onDeleted,
}: {
  asset: MediaAsset | null;
  onClose: () => void;
  onChanged: () => void;
  onDeleted: () => void;
}) {
  const [title, setTitle] = useState("");
  const [altText, setAltText] = useState("");
  const [tags, setTags] = useState("");
  const [usages, setUsages] = useState<MediaUsage[] | null>(null);
  const [loadingUsage, setLoadingUsage] = useState(false);
  // "delete" asks before the first attempt; "force" is the second ask after
  // the server reported the file is still referenced.
  const [confirmMode, setConfirmMode] = useState<null | "delete" | "force">(null);
  const [transcriptOpen, setTranscriptOpen] = useState(false);

  useEffect(() => {
    if (!asset) return;
    setTitle(asset.title ?? "");
    setAltText(asset.alt_text ?? "");
    setTags(asset.tags ?? "");
    setUsages(null);
    setConfirmMode(null);
    let cancelled = false;
    setLoadingUsage(true);
    getMediaUsage({ data: { id: asset.id } })
      .then((r) => {
        if (!cancelled) setUsages(r.usages);
      })
      .catch(() => {
        if (!cancelled) setUsages(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingUsage(false);
      });
    return () => {
      cancelled = true;
    };
  }, [asset]);

  const dirty =
    !!asset &&
    (title !== (asset.title ?? "") ||
      altText !== (asset.alt_text ?? "") ||
      tags !== (asset.tags ?? ""));

  const save = useMutation({
    mutationFn: async () => {
      if (!asset) return;
      await updateMediaAsset({
        data: { id: asset.id, title: title || null, alt_text: altText || null, tags: tags || null },
      });
    },
    onSuccess: () => {
      toast.success("Details saved");
      onChanged();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Could not save"),
  });

  const remove = useMutation({
    mutationFn: async (force: boolean) => {
      if (!asset) return;
      await deleteMediaAsset({ data: { id: asset.id, force } });
    },
    onSuccess: () => {
      toast.success("File deleted");
      setConfirmMode(null);
      onDeleted();
    },
    onError: async (e) => {
      // The server refuses to delete a referenced asset; show what points at it
      // and let the admin confirm a forced delete.
      if (e instanceof ApiError && e.status === 409 && asset) {
        try {
          const r = await getMediaUsage({ data: { id: asset.id } });
          setUsages(r.usages);
        } catch {
          // fall through — the confirm dialog still explains the block
        }
        setConfirmMode("force");
        return;
      }
      setConfirmMode(null);
      toast.error(e instanceof Error ? e.message : "Could not delete");
    },
  });

  const url = asset ? mediaAssetUrl(asset) : "";

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(new URL(url, window.location.origin).toString());
      toast.success("URL copied");
    } catch {
      toast.error("Could not copy — select the URL field and copy manually");
    }
  };

  return (
    <>
      <Sheet open={!!asset} onOpenChange={(v) => !v && onClose()}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
          {asset && (
            <>
              <SheetHeader className="pr-8">
                <SheetTitle className="truncate">{mediaAssetLabel(asset)}</SheetTitle>
                <SheetDescription className="truncate">{asset.s3_key}</SheetDescription>
              </SheetHeader>

              <div className="mt-4 space-y-5">
                <div className="grid max-h-72 place-items-center overflow-hidden rounded-lg border bg-muted">
                  {isVideoAsset(asset) ? (
                    <video src={url} controls preload="metadata" className="max-h-72 w-full" />
                  ) : asset.mime === "application/pdf" ? (
                    <a
                      href={url}
                      target="_blank"
                      rel="noreferrer"
                      className="grid place-items-center gap-2 p-10 text-sm text-muted-foreground hover:text-foreground"
                    >
                      <ExternalLink className="h-6 w-6" />
                      Open PDF
                    </a>
                  ) : (
                    <img src={url} alt={asset.alt_text ?? ""} className="max-h-72 w-full object-contain" />
                  )}
                </div>

                <div className="flex flex-wrap gap-1.5">
                  <Badge variant="secondary">{asset.folder}</Badge>
                  {asset.mime && <Badge variant="outline">{asset.mime}</Badge>}
                  <Badge variant="outline">{formatBytes(asset.size_bytes)}</Badge>
                  {asset.width && asset.height && (
                    <Badge variant="outline">
                      {asset.width}×{asset.height}
                    </Badge>
                  )}
                  <Badge variant="outline">{asset.source}</Badge>
                  {asset.thumb_status !== "done" && asset.thumb_status !== "n-a" && (
                    <Badge variant="outline">thumb: {asset.thumb_status}</Badge>
                  )}
                </div>

                <div className="grid gap-1.5">
                  <Label htmlFor="media-url">URL</Label>
                  <div className="flex gap-2">
                    <Input id="media-url" readOnly value={url} className="font-mono text-xs" />
                    <Button type="button" variant="outline" size="icon" onClick={copyUrl} title="Copy URL">
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button type="button" variant="outline" size="icon" asChild title="Open in new tab">
                      <a href={url} target="_blank" rel="noreferrer">
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    </Button>
                  </div>
                </div>

                {isCaptionsAsset(asset) && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="w-fit"
                    onClick={() => setTranscriptOpen(true)}
                  >
                    <FileText className="mr-1 h-3.5 w-3.5" /> Edit captions
                  </Button>
                )}

                <div className="grid gap-3 rounded-lg border p-3">
                  <div className="grid gap-1.5">
                    <Label htmlFor="media-title">Title (optional)</Label>
                    <Input
                      id="media-title"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder={asset.filename}
                    />
                  </div>
                  <div className="grid gap-1.5">
                    <Label htmlFor="media-alt">Alt text (optional)</Label>
                    <Textarea
                      id="media-alt"
                      rows={2}
                      value={altText}
                      onChange={(e) => setAltText(e.target.value)}
                      placeholder="What the image shows, for screen readers"
                    />
                  </div>
                  <div className="grid gap-1.5">
                    <Label htmlFor="media-tags">Tags (optional)</Label>
                    <Input
                      id="media-tags"
                      value={tags}
                      onChange={(e) => setTags(e.target.value)}
                      placeholder="comma, separated, keywords"
                    />
                  </div>
                  <Button
                    type="button"
                    disabled={!dirty || save.isPending}
                    onClick={() => save.mutate()}
                    title={dirty ? undefined : "No changes to save"}
                  >
                    {save.isPending ? "Saving…" : "Save details"}
                  </Button>
                </div>

                <div className="grid gap-2 rounded-lg border p-3">
                  <p className="text-sm font-medium">Used in</p>
                  {loadingUsage ? (
                    <p className="text-sm text-muted-foreground">
                      <Loader2 className="mr-1 inline h-3 w-3 animate-spin" /> Checking…
                    </p>
                  ) : usages && usages.length > 0 ? (
                    <ul className="space-y-1 text-sm">
                      {usages.map((u, i) => (
                        <li key={`${u.table}-${u.rowId}-${u.column}-${i}`} className="flex gap-2">
                          <Badge variant="outline" className="shrink-0 text-[10px]">
                            {u.table}
                          </Badge>
                          <span className="truncate text-muted-foreground">{u.label}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      Not referenced anywhere — safe to delete.
                    </p>
                  )}
                </div>

                <Button
                  type="button"
                  variant="destructive"
                  className="w-full"
                  disabled={remove.isPending}
                  onClick={() => setConfirmMode("delete")}
                >
                  <Trash2 className="mr-1 h-4 w-4" />
                  {remove.isPending ? "Deleting…" : "Delete file"}
                </Button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <AlertDialog
        open={confirmMode !== null}
        onOpenChange={(v) => {
          if (!v && !remove.isPending) setConfirmMode(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmMode === "force"
                ? "This file is still in use"
                : `Delete "${asset ? mediaAssetLabel(asset) : "this file"}"?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmMode === "force"
                ? "Deleting it will leave broken images on the pages below. This cannot be undone."
                : "The object is removed from S3 and the library, along with its thumbnail. This cannot be undone."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {confirmMode === "force" && usages && usages.length > 0 && (
            <ul className="max-h-48 space-y-1 overflow-y-auto rounded-md border p-2 text-sm">
              {usages.map((u, i) => (
                <li key={`${u.table}-${u.rowId}-${u.column}-${i}`} className="flex gap-2">
                  <Badge variant="outline" className="shrink-0 text-[10px]">
                    {u.table}
                  </Badge>
                  <span className="truncate text-muted-foreground">{u.label}</span>
                </li>
              ))}
            </ul>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={remove.isPending}>Keep the file</AlertDialogCancel>
            <AlertDialogAction
              disabled={remove.isPending}
              onClick={(e) => {
                e.preventDefault();
                remove.mutate(confirmMode === "force");
              }}
            >
              {remove.isPending
                ? "Deleting…"
                : confirmMode === "force"
                  ? "Delete anyway"
                  : "Delete file"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {asset && isCaptionsAsset(asset) && (
        <CaptionEditorDialog
          open={transcriptOpen}
          onOpenChange={setTranscriptOpen}
          {...assetBucketAndPath(asset)}
          onSaved={() => onChanged()}
        />
      )}
    </>
  );
}

// ----------------------------------------------------------- upload dialog

type QueuedUpload = {
  file: File;
  percent: number;
  /** "unregistered": bytes are in S3 but the row failed — Backfill picks it up. */
  status: "queued" | "uploading" | "done" | "unregistered" | "error";
  message?: string;
  /** Shown under the bar: a large upload continued or restarted an earlier one. */
  note?: string;
  /** Bytes are up; the object is being completed and registered. */
  finishing?: boolean;
};

function UploadDialog({
  open,
  onOpenChange,
  defaultFolder,
  folderPaths,
  onFolderCreated,
  onDone,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  /** The library's current folder filter ("all" when none). */
  defaultFolder: string;
  /** Every known folder path; sub-folders of nested uploadable buckets become destinations. */
  folderPaths: readonly string[];
  /** A folder was created from the inline "New folder" row. */
  onFolderCreated: (path: string) => void;
  onDone: () => void;
}) {
  const qc = useQueryClient();
  const { buckets, knownPaths } = useMediaDestinationSources(folderPaths);
  const [folder, setFolder] = useState("");
  const [queue, setQueue] = useState<QueuedUpload[]>([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  // Kinds of the chosen files; destinations that cannot take all of them are
  // shown disabled with the reason.
  const queuedKinds = useMemo(() => {
    const kinds = new Set<MediaKind>();
    for (const item of queue) {
      const k = mediaKindOfType(detectMediaType(item.file));
      if (k) kinds.add(k);
    }
    return [...kinds];
  }, [queue]);

  const options = useMemo(
    () =>
      mediaDestinationOptions(buckets, knownPaths, {
        kinds: queuedKinds,
        // The highlighted folder and the current choice must stay listed
        // even if the folder list lags behind.
        ensure: [defaultFolder, folder],
      }),
    [buckets, knownPaths, queuedKinds, defaultFolder, folder],
  );
  const chosen = options.find((o) => o.path === folder);
  const destinationError = !folder
    ? "Choose a destination folder."
    : !chosen
      ? "This folder does not take uploads."
      : chosen.disabledReason
        ? `${mediaFolderLabel(buckets, folder.split("/")[0])}: ${chosen.disabledReason.toLowerCase()}. Choose another folder.`
        : null;

  useEffect(() => {
    if (open) {
      setFolder(
        pickDefaultDestination(
          mediaDestinationOptions(buckets, knownPaths, { ensure: [defaultFolder] }),
          buckets,
          defaultFolder,
        ),
      );
      setQueue([]);
    }
    // `buckets`/`knownPaths` are deliberately left out: a refresh mid-session
    // must not reset a folder the admin already chose.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultFolder]);

  const patch = (index: number, changes: Partial<QueuedUpload>) =>
    setQueue((prev) => prev.map((item, i) => (i === index ? { ...item, ...changes } : item)));

  const run = async () => {
    if (queue.length === 0 || destinationError) return;
    setBusy(true);
    let uploaded = 0;
    let unregistered = 0;
    for (let i = 0; i < queue.length; i += 1) {
      const item = queue[i];
      if (item.status === "done" || item.status === "unregistered") continue;
      // The Media Library's folder select takes any kind the bucket allows;
      // the kind comes from the file itself and uploadMedia enforces the rest.
      const kind = mediaKindOfType(detectMediaType(item.file));
      if (!kind) {
        patch(i, { status: "error", message: "Unsupported file type" });
        continue;
      }
      // A retry re-sends the same File object, so a large upload that stopped
      // part-way continues from the parts already in S3.
      patch(i, {
        status: "uploading",
        percent: 0,
        message: undefined,
        note: undefined,
        finishing: false,
      });
      try {
        await uploadMedia(
          item.file,
          { ...splitDestination(folder), kind },
          {
            onProgress: (p) => patch(i, { percent: p.percent }),
            onPhase: (phase) => patch(i, { finishing: phase === "finishing" }),
            onResume: ({ restarted, percent }) =>
              patch(i, {
                note: restarted
                  ? MULTIPART_RESTARTED_MESSAGE
                  : percent > 0
                    ? `Continuing the earlier upload from ${percent}%.`
                    : undefined,
              }),
          },
        );
        patch(i, { status: "done", percent: 100 });
        uploaded += 1;
      } catch (e) {
        if (e instanceof MediaUploadError && e.code === "REGISTER_FAILED") {
          patch(i, { status: "unregistered", percent: 100, message: "Uploaded, not registered" });
          unregistered += 1;
          continue;
        }
        patch(i, {
          status: "error",
          message: e instanceof Error ? e.message : "Upload failed",
        });
      }
    }
    setBusy(false);
    if (uploaded > 0) {
      toast.success(`${uploaded} file${uploaded === 1 ? "" : "s"} uploaded`);
    }
    if (unregistered > 0) {
      toast.warning(
        `${unregistered} file${unregistered === 1 ? " was" : "s were"} uploaded but could not be registered. Run Maintenance > Backfill.`,
      );
    }
    if (uploaded > 0 || unregistered > 0) {
      onDone();
      void qc.invalidateQueries({ queryKey: ["media_folders"] });
    }
  };

  const allDone =
    queue.length > 0 && queue.every((i) => i.status === "done" || i.status === "unregistered");
  // Only failed items left: the run button retries them (same File objects).
  const failedCount = queue.filter((i) => i.status === "error").length;
  const retryOnly = failedCount > 0 && !queue.some((i) => i.status === "queued");

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      size="md"
      icon={Upload}
      title="Upload media"
      context="Images and PDFs up to 25 MB, video up to 10 GB, captions (.vtt) up to 2 MB"
      busy={busy}
      footer={
        <>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            {allDone ? "Close" : "Cancel"}
          </Button>
          <Button
            type="button"
            onClick={run}
            disabled={busy || queue.length === 0 || allDone || !!destinationError}
            title={destinationError ?? undefined}
          >
            {busy ? (
              <>
                <Loader2 className="mr-1 h-4 w-4 animate-spin" /> Uploading…
              </>
            ) : retryOnly ? (
              <>
                <RotateCw className="mr-1 h-4 w-4" /> Retry {failedCount}
              </>
            ) : (
              <>
                <Upload className="mr-1 h-4 w-4" /> Upload {queue.length || ""}
              </>
            )}
          </Button>
        </>
      }
    >
      <p className="mb-4 text-sm text-muted-foreground">
        Large videos upload in parts. If an upload stops, click Retry or choose the same file
        again to continue where it left off. Files over these limits go through the CLI and then
        Maintenance &gt; Backfill.
      </p>
    <div className="grid gap-4">
      <div className="grid gap-1.5">
        <Label htmlFor="upload-folder">Destination folder</Label>
        <MediaDestinationSelect
          id="upload-folder"
          value={folder}
          onChange={setFolder}
          options={options}
          buckets={buckets}
          knownPaths={knownPaths}
          disabled={busy}
          invalid={queue.length > 0 && !!destinationError}
          onFolderCreated={onFolderCreated}
        />
        {queue.length > 0 && destinationError ? (
          <p className="text-xs text-destructive">{destinationError}</p>
        ) : (
          <p className="text-xs text-muted-foreground">
            Archive folders are read-only and not listed. Some folders take only certain file
            types or keep a flat layout without sub-folders.
          </p>
        )}
        <div className="grid gap-1">
          <span className="text-xs font-medium text-muted-foreground">Saved as</span>
          <code className="break-all rounded-md bg-muted px-2 py-1.5 font-mono text-xs">
            {destinationPreview(
              folder,
              queue.length === 1 ? queue[0].file.name : undefined,
            )}
            {queue.length > 1 && (
              <span className="font-sans text-muted-foreground"> (and {queue.length - 1} more)</span>
            )}
          </code>
        </div>
      </div>

      <div className="grid gap-1.5">
        <Label htmlFor="upload-files">Files</Label>
        <Input
          id="upload-files"
          ref={fileRef}
          type="file"
          multiple
          accept={MEDIA_ACCEPT_ATTRIBUTE}
          disabled={busy}
          onChange={(e) =>
            setQueue(
              Array.from(e.target.files ?? []).map((file) => ({
                file,
                percent: 0,
                status: "queued" as const,
              })),
            )
          }
        />
      </div>

      {queue.length > 0 && (
        <ul className="space-y-2 rounded-lg border p-3">
          {queue.map((item, i) => (
            <li key={`${item.file.name}-${i}`} className="grid gap-1">
              <div className="flex items-center justify-between gap-2 text-xs">
                <span className="truncate">{item.file.name}</span>
                <span
                  className={cn(
                    "shrink-0",
                    item.status === "error"
                      ? "text-destructive"
                      : item.status === "unregistered"
                        ? "text-amber-700 dark:text-amber-400"
                        : "text-muted-foreground",
                  )}
                >
                  {item.status === "error"
                    ? "Failed"
                    : item.status === "unregistered"
                      ? item.message
                      : item.status === "done"
                        ? "Done"
                        : item.status === "uploading"
                          ? item.finishing
                            ? "Finishing…"
                            : `${item.percent}%`
                          : formatBytes(item.file.size)}
                </span>
              </div>
              {item.status !== "queued" && item.status !== "error" && (
                <Progress value={item.percent} />
              )}
              {item.status === "error" && item.message && (
                <p className="text-xs text-destructive">{item.message}</p>
              )}
              {item.note && item.status !== "error" && (
                <p className="text-xs text-muted-foreground">{item.note}</p>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>

    </FormDialog>
  );
}

// ------------------------------------------------------- maintenance menu

function MaintenanceMenu({ onDone }: { onDone: () => void }) {
  const [busy, setBusy] = useState<null | "backfill" | "thumbs">(null);

  // Both actions are paged server-side; loop until the server says it is done.
  const runBackfill = async () => {
    setBusy("backfill");
    const id = toast.loading("Scanning S3 for unregistered files…");
    let scanned = 0;
    let registered = 0;
    let token: string | undefined;
    try {
      for (let pass = 0; pass < 200; pass += 1) {
        const r = await backfillMediaAssets({ data: { continuationToken: token } });
        scanned += r.scanned;
        registered += r.registered;
        toast.loading(`Scanned ${scanned} objects, registered ${registered}…`, { id });
        token = r.nextToken;
        if (!token) break;
      }
      toast.success(`Backfill complete — ${registered} new file${registered === 1 ? "" : "s"} registered.`, { id });
      onDone();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Backfill failed", { id });
    } finally {
      setBusy(null);
    }
  };

  const runThumbnails = async () => {
    setBusy("thumbs");
    const id = toast.loading("Generating thumbnails…");
    let processed = 0;
    let failed = 0;
    try {
      for (let pass = 0; pass < 200; pass += 1) {
        const r = await generateMissingThumbnails({ data: { limit: 20 } });
        processed += r.processed;
        failed += r.failed;
        toast.loading(`${processed} generated, ${r.remaining} remaining…`, { id });
        // Stop if a pass makes no progress, so a permanently failing asset
        // cannot spin this loop forever.
        if (r.remaining === 0 || r.processed + r.failed === 0) break;
      }
      toast.success(
        `Thumbnails done — ${processed} generated${failed ? `, ${failed} failed` : ""}.`,
        { id },
      );
      onDone();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Thumbnail generation failed", { id });
    } finally {
      setBusy(null);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="sm" variant="outline" disabled={!!busy}>
          {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Wrench className="mr-1 h-4 w-4" />}
          Maintenance
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel>Library maintenance</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => void runBackfill()} className="gap-2">
          <RefreshCw className="h-4 w-4" />
          <div>
            <p className="text-sm">Backfill registry</p>
            <p className="text-xs text-muted-foreground">Index files already in S3</p>
          </div>
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => void runThumbnails()} className="gap-2">
          <Sparkles className="h-4 w-4" />
          <div>
            <p className="text-sm">Generate thumbnails</p>
            <p className="text-xs text-muted-foreground">Fill in missing previews</p>
          </div>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}
